import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton, Stack, Chip } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { AppointmentCompletionReport } from '../../../types/reporting';

interface AppointmentCompletionChartProps {
  data?: AppointmentCompletionReport;
  loading?: boolean;
}

const AppointmentCompletionChart: React.FC<AppointmentCompletionChartProps> = ({
  data,
  loading = false,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const rows = (data?.users ?? []).map((u) => ({
    userName: u.userName,
    completionRate: u.completionRate,
    completed: u.completedAppointments,
    noShow: u.noShowAppointments,
    cancelled: u.cancelledAppointments,
    pending: u.pendingAppointments,
  }));

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Appointment Completion by User</Typography>
          {data && (
            <Chip
              size="small"
              label={`Overall: ${data.overallCompletionRate.toFixed(1)}%`}
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Completion rate = Completed / (Completed + No-show + Cancelled). Pending appointments
          are excluded from the rate.
        </Typography>
        <Box sx={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 10, right: 30, left: 20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={{ stroke: theme.palette.divider }}
              />
              <YAxis
                type="category"
                dataKey="userName"
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={{ stroke: theme.palette.divider }}
                width={120}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 4,
                }}
                labelStyle={{ color: theme.palette.text.primary }}
                formatter={(value: number, name: string, entry: any) => {
                  if (name === 'Completion rate') {
                    return [
                      `${value.toFixed(1)}% (${entry.payload.completed} of ${entry.payload.completed + entry.payload.noShow + entry.payload.cancelled} resolved)`,
                      name,
                    ];
                  }
                  return [value, name];
                }}
              />
              <Legend />
              <Bar
                dataKey="completionRate"
                name="Completion rate"
                fill={theme.palette.primary.main}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AppointmentCompletionChart;
